import React, { useEffect } from 'react';
import CurvedLines from '../ui/CurvedLines';
import { useLocation, Link } from 'react-router-dom';

const footerLinkStyle = `
  .footer-custom-link {
    position: relative;
    display: inline-block;
    width: fit-content;
    text-decoration: none;
  }

  .footer-custom-link::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, #ef4444, #dc2626);
    transition: transform 0.3s ease;
    transform-origin: left;
    transform: scaleX(0);
  }
  
  .footer-custom-link.active-footer-link::after {
    transform: scaleX(1);
  }
  
  .footer-custom-link:hover::after {
    transform: scaleX(1);
  }
`;

const Footer = () => {
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = footerLinkStyle;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const FooterLink = ({ href, children, className }) => {
    const isInternal = href.startsWith('/');
    const isActive = location.pathname === href;

    const baseStyle = className || "text-gray-400 hover:text-white mb-2 text-sm";

    let finalClasses = `${baseStyle} transition-colors footer-custom-link cursor-pointer`;

    if (isActive) {
      finalClasses += " active-footer-link text-white";
    }

    if (isInternal) {
      return (
          <Link to={href} className={finalClasses}>
            {children}
          </Link>
      );
    }

    return (
        <a href={href} className={finalClasses} target={href.startsWith('http') ? "_blank" : "_self"} rel="noopener noreferrer">
          {children}
        </a>
    );
  };

  return (
      <footer className="w-full bg-black relative pt-20 pb-10 overflow-hidden">

        
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-red-900/20 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <CurvedLines count={2} color="#ef4444" opacity={0.1} animate={true} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">

            
            <div className="lg:col-span-4">
              <Link to="/" className="inline-block mb-6 group">
                <div className="flex items-center">
                  <img
                      src="/logo.png"
                      alt="Xf "
                      className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105 mr-4"
                  />
                  <span className="text-white text-2xl font-bold tracking-tight">Xfeatures</span>
                </div>
              </Link>
              <p className="text-white/60 mb-2 leading-relaxed text-sm">
                Инфраструктурный холдинг нового поколения.
              </p>
              <p className="text-white/60 mb-6 leading-relaxed text-sm">
                Мы объединяем мощное железо, AI-технологии и кибербезопасность.
              </p>

              <a
                  href="https://xfeatures.net/status/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-green-500/30 transition-all group"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-xs font-mono text-white/80 group-hover:text-green-400 transition-colors">All Systems Operational</span>
              </a>
            </div>

            <div className="lg:col-span-2 lg:col-start-6">
              <h3 className="text-white font-bold mb-6">Продукты</h3>
              <ul className="space-y-2">
                <li><FooterLink href="/hosting">VDS Хостинг</FooterLink></li>
                <li><FooterLink href="#">XfeaturesAI</FooterLink></li>
                <li><FooterLink href="#">XfeaturesGUARD</FooterLink></li>
                <li><FooterLink href="#">API для разработчиков</FooterLink></li>
              </ul>
            </div>

            
            <div className="lg:col-span-2">
              <h3 className="text-white font-bold mb-6">Компания</h3>
              <ul className="space-y-2">
                <li><FooterLink href="/about">О нас</FooterLink></li>
                <li><FooterLink href="/about#team">Команда</FooterLink></li>
                <li><FooterLink href="#">Карьера</FooterLink></li>
                <li><FooterLink href="#">Блог</FooterLink></li>
              </ul>
            </div>

            
            <div className="lg:col-span-2">
              <h3 className="text-white font-bold mb-6">Поддержка</h3>
              <ul className="space-y-2">
                <li><FooterLink href="/support">Центр помощи</FooterLink></li>
                <li><FooterLink href="https://discord.gg/J7pQys8K47">Discord Community</FooterLink></li>
                <li><FooterLink href="/status">Статус сети</FooterLink></li>
                <li><FooterLink href="mailto:support@xfeatures.net">support@xfeatures.net</FooterLink></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/40 text-xs">
              © {currentYear} Xfeatures Group. Все права защищены.
            </div>

            <div className="flex gap-6">
              
              <FooterLink href="/privacy" className="text-xs text-white/40 hover:text-red-400">Privacy Policy</FooterLink>
              <FooterLink href="/terms" className="text-xs text-white/40 hover:text-red-400">Terms of Service</FooterLink>
              <FooterLink href="/sla" className="text-xs text-white/40 hover:text-red-400">SLA</FooterLink>
            </div>

            <div className="flex items-center gap-4">
              <a href="#" className="text-white/40 hover:text-red-400 transition-colors">
                <span className="sr-only">GitHub</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" /></svg>
              </a>
              <a href="#" className="text-white/40 hover:text-[#5865F2] transition-colors">
                <span className="sr-only">Discord</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.39-.444.885-.608 1.283a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.283.077.077 0 0 0-.079-.036c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026c.462-.62.874-1.275 1.226-1.963.021-.04.001-.088-.041-.104a13.202 13.202 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" /></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
  );
};

export default Footer;